import {useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {getSearchResultPath} from 'src/hooks/common-data-service/cached-table/utils';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';

import type {
    CacheKeys,
    CachedTableActions,
    CachedTableDataSelector,
    CachedTableResultSelector,
    CachedTableStateSelector,
} from './typedef';
import type {Error} from 'src/openapi/admin/models/Error';


type Props<Result, Data, SearchFields, SortFields, Filters> = {
    resultSelector: CachedTableResultSelector<Data>,
    stateSelector: CachedTableStateSelector<SearchFields, SortFields, Filters>,
    dataSelector: CachedTableDataSelector<Data>,
    actions: CachedTableActions<Data, SearchFields, SortFields, Filters>,
    getAction: (cacheKeys: CacheKeys<SearchFields, SortFields, Filters>) => Promise<{
        type: string,
        payload: {
            data: Result,
            success: true,
            error: false,
        } | {
            data: Error,
            success: false,
            error: true,
        },
    }>,
    onSuccessLoad: (data: Result, cacheParams: CacheKeys<SearchFields, SortFields, Filters>) => void,
    shouldUpdateData?: boolean,
    shouldClearOnUnmount?: boolean,
};

export const useCachedTable = <Result, Data, SearchFields, SortFields, Filters>(
    props: Props<Result, Data, SearchFields, SortFields, Filters>,
) => {
    const {
        resultSelector,
        stateSelector,
        dataSelector,
        getAction,
        actions,
        onSuccessLoad,
        shouldClearOnUnmount = true,
        shouldUpdateData = true,
    } = props;

    const dispatch = useDispatch();

    const {
        isLoading,
        cache,
        isRefreshRequired,
        loadingError,
    } = useSelector(dataSelector);
    const cacheKeys = useSelector(stateSelector);
    const result = useSelector(resultSelector);

    const loadData = useCallback(async (cacheParams: CacheKeys<SearchFields, SortFields, Filters>) => {
        dispatch(actions.startLoading());
        const response = await getAction(cacheParams);

        if (response.payload.success) {
            if (response.payload.data) {
                onSuccessLoad(response.payload.data, cacheParams);
            } else {
                dispatch(actions.onFailure({message: 'No data received from the server'}));
            }
        } else {
            dispatch(actions.onFailure(response.payload.data));
        }
    }, [getAction, onSuccessLoad, actions, dispatch]);

    useEffect(() => {
        const newCacheKeys = {...cacheKeys};

        if (shouldUpdateData && !cache.find(({key}) => getSearchResultPath(cacheKeys) === key)) {
            loadData(newCacheKeys);
        }

        return () => {
            if (shouldClearOnUnmount) {
                dispatch(actions.clearData());
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffectWithDeps((prev, current) => {
        if (
            current.shouldUpdateData &&
            JSON.stringify(prev.cacheKeys) !== JSON.stringify(current.cacheKeys) &&
            !current.cache.find(({key}) => getSearchResultPath(current.cacheKeys) === key)
            || (prev.isRefreshRequired !== current.isRefreshRequired && current.isRefreshRequired)
        ) {
            current.loadData(current.cacheKeys);
        }
    }, {cacheKeys, cache, isRefreshRequired, shouldUpdateData, loadData});

    return {
        isLoading,
        loadingError,
        result,

        pagination: cacheKeys.pagination,
        loadData,
        cacheKeys,
    };
};
